import commaNumber from 'comma-number';
import type { ManipulateType } from 'dayjs';
import { MoreVertical } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import IconButton from '@/components/Button/IconButton';
import Menu from '@/components/Menu/Menu';
import { MenuItemType } from '@/components/Menu/types';
import { useFeedbackTerminology } from '@/entities/workspace/hooks';
import type {
  ChartSeries,
  LineChartResetController,
  TooltipInfo,
} from '@/shared/ui/uplot-chart';
import { LineChart } from '@/shared/ui/uplot-chart';
import { getChartColorByHash } from '@/styles/colors/chartColors';
import zIndex from '@/styles/zIndex';

import type { ChartMappedIssue } from '../../hooks/use-chart-mapped-data';
import { useChartMappedData } from '../../hooks/use-chart-mapped-data';
import { useIssueListActions } from '../../hooks/use-issue-list-query';
import {
  getGroupedTooltipPoints,
  sortTooltipPointsByIssueRank,
} from '../../lib/chart-tooltip.utils';
import {
  getPeriodLabel,
  parseSignalDatePreset,
} from '../../lib/signal-date-preset.utils';
import type {
  CompositedSignalIssue,
  SignalDateSelectionInput,
} from '../../model';
import { ChangesLabel } from '../changes-label';
import { IssueLegend } from '../chart.issue-legend';

/** x축 포맷과 동일한 규칙으로 툴팁 시간 레이블을 생성한다 */
const formatTooltipTime = (
  timestampMs: number,
  manipulateType: ManipulateType,
): string => {
  const date = new Date(timestampMs);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  if (manipulateType === 'hour') {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const hour12 = hours % 12 === 0 ? 12 : hours % 12;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const timeStr =
      minutes === 0
        ? `${hour12}${ampm}`
        : `${hour12}:${String(minutes).padStart(2, '0')}${ampm}`;
    return `${month}/${day} ${timeStr}`;
  }

  return `${month}/${day}`;
};

export const ChartView = (props: {
  data: CompositedSignalIssue[];
  dynamicFilterSet: StructuredFilter;
  datePreset: SignalDateSelectionInput;
  sortStatus?: NotNullableSortStatusT;
  topCount?: number;
}) => {
  const manipulateType = parseSignalDatePreset(props.datePreset).manipulateType;

  const slicedData = props.data.slice(0, props.topCount);

  // 차트 렌더링을 위해 mappedData 추가 + 정렬 및 topCount 적용
  const chartMappedIssues = useChartMappedData(slicedData, props.datePreset, {
    sortStatus: props.sortStatus,
    topCount: props.topCount,
  });

  const chartSeries: ChartSeries[] = useMemo(
    () =>
      chartMappedIssues.map((issue) => ({
        id: issue.name,
        label: issue.name,
        data: issue.mappedData.map((d) => ({
          x: d.timestamp,
          y: d.value,
        })),
        color: getChartColorByHash(issue.name),
      })),
    [chartMappedIssues],
  );

  const [hiddenSeriesIds, setHiddenSeriesIds] = useState<Set<string>>(
    new Set(),
  );
  const [resetController, setResetController] =
    useState<LineChartResetController>({
      enabled: false,
      reset: () => undefined,
    });
  const [_isolatedSeriesId, setIsolatedSeriesId] = useState<string | null>(
    null,
  );
  const [preIsolateHiddenIds, setPreIsolateHiddenIds] =
    useState<Set<string> | null>(null);

  const handleToggleSeries = useCallback(
    (issueName: string) => {
      setHiddenSeriesIds((prev) => {
        const visibleCount = chartSeries.filter((s) => !prev.has(s.id)).length;
        const isCurrentlyVisible = !prev.has(issueName);
        // Guard: don't hide if it's the last visible series
        if (isCurrentlyVisible && visibleCount === 1) return prev;
        const next = new Set(prev);
        if (next.has(issueName)) {
          next.delete(issueName);
        } else {
          next.add(issueName);
        }
        return next;
      });
    },
    [chartSeries],
  );

  const handleIsolateSeries = useCallback(
    (issueName: string) => {
      setIsolatedSeriesId((currentIsolated) => {
        if (currentIsolated === null) {
          // Not in isolate mode: enter isolate mode
          setPreIsolateHiddenIds(new Set(hiddenSeriesIds));
          const allSeriesIds = new Set(chartSeries.map((s) => s.id));
          const hiddenSet = new Set(allSeriesIds);
          hiddenSet.delete(issueName);
          setHiddenSeriesIds(hiddenSet);
          return issueName;
        } else if (currentIsolated === issueName) {
          // Same series clicked: exit isolate mode
          setHiddenSeriesIds(preIsolateHiddenIds ?? new Set());
          setPreIsolateHiddenIds(null);
          return null;
        } else {
          // Different series clicked: switch isolate target
          const allSeriesIds = new Set(chartSeries.map((s) => s.id));
          const hiddenSet = new Set(allSeriesIds);
          hiddenSet.delete(issueName);
          setHiddenSeriesIds(hiddenSet);
          return issueName;
        }
      });
    },
    [chartSeries, hiddenSeriesIds, preIsolateHiddenIds],
  );

  const handleShowAll = useCallback(() => {
    setHiddenSeriesIds(new Set());
    setIsolatedSeriesId(null);
    setPreIsolateHiddenIds(null);
  }, []);

  return (
    <div className="flex flex-col gap-6 p-10">
      <div className="relative w-full h-120 z-[1]">
        <LineChart
          series={chartSeries}
          hiddenSeriesIds={hiddenSeriesIds}
          xAxis={{
            type: 'time',
            timeUnit: manipulateType as 'hour' | 'day' | 'week',
          }}
          enableZoom
          enablePan={false}
          onReset={handleShowAll}
          onResetControllerChange={setResetController}
          tooltipContent={(info, actions) => (
            <SignalChartTooltip
              info={info}
              issues={chartMappedIssues}
              dynamicFilterSet={props.dynamicFilterSet}
              datePreset={props.datePreset}
              manipulateType={manipulateType}
              lock={actions?.lock}
              unlock={actions?.unlock}
              onToggleSeries={handleToggleSeries}
              onIsolateSeries={handleIsolateSeries}
              onResetAll={resetController.reset}
              isResetEnabled={resetController.enabled}
              hiddenSeriesIds={hiddenSeriesIds}
              totalSeriesCount={chartSeries.length}
            />
          )}
        />
      </div>
      <IssueLegend
        {...props}
        data={chartMappedIssues}
        onToggle={handleToggleSeries}
        hiddenIssueNames={hiddenSeriesIds}
        onIsolate={handleIsolateSeries}
        onShowAll={resetController.reset}
        mode="reset"
        isResetEnabled={resetController.enabled}
      />
    </div>
  );
};

const SignalChartTooltip = (props: {
  info: TooltipInfo;
  issues: ChartMappedIssue[];
  dynamicFilterSet: StructuredFilter;
  datePreset: SignalDateSelectionInput;
  manipulateType: ManipulateType;
  lock?: () => void;
  unlock?: () => void;
  onToggleSeries?: (issueName: string) => void;
  onIsolateSeries?: (issueName: string) => void;
  onResetAll?: () => void;
  isResetEnabled?: boolean;
  hiddenSeriesIds?: Set<string>;
  totalSeriesCount?: number;
}) => {
  const { favoriteIssue, hideIssue, navigateToIssueDetailPage } =
    useIssueListActions();
  const { replaceFeedbackTerminology } = useFeedbackTerminology();

  const issueName =
    props.info.activeSeriesId ?? props.info.points[0]?.seriesId ?? '';

  const visibleCount =
    (props.totalSeriesCount ?? 0) - (props.hiddenSeriesIds?.size ?? 0);

  const groupedPointsInfo = useMemo(
    () =>
      getGroupedTooltipPoints({
        activeSeriesId: issueName,
        points: props.info.points,
      }),
    [issueName, props.info.points],
  );
  const bucketValue = groupedPointsInfo.bucketValue;
  const timeLabel = formatTooltipTime(props.info.xValue, props.manipulateType);
  const periodLabel = getPeriodLabel(props.datePreset);
  const issueMap = useMemo(
    () => new Map(props.issues.map((item) => [item.name, item])),
    [props.issues],
  );
  const issueIndexBySeriesId = useMemo(
    () =>
      new Map(
        props.issues.map((item) => [item.name, item.issueIndex] as const),
      ),
    [props.issues],
  );
  const orderedPoints = useMemo(() => {
    return sortTooltipPointsByIssueRank({
      activeSeriesId: groupedPointsInfo.activeSeriesId,
      issueIndexBySeriesId,
      points: groupedPointsInfo.points,
    });
  }, [
    groupedPointsInfo.activeSeriesId,
    groupedPointsInfo.points,
    issueIndexBySeriesId,
  ]);

  return (
    <div
      className="flex flex-col shadow-2xs bg-white rounded-lg px-1 py-2 min-w-50 gap-1 h-fit max-h-96 overflow-y-auto"
      style={{
        zIndex: zIndex.chartTooltip + 100,
      }}
    >
      <p className="text-xs text-gray-500 pb-0.5">{timeLabel}</p>
      <p className="text-xs text-gray-600 pb-1">
        {replaceFeedbackTerminology('Feedback count')}: {commaNumber(bucketValue)}
      </p>
      <div className="flex flex-col gap-2">
        {orderedPoints.map((point) => {
          const pointIssue = issueMap.get(point.seriesId);
          const pointIssueName = point.seriesId;
          const isPointHidden =
            props.hiddenSeriesIds?.has(pointIssueName) ?? false;
          const isPointLastVisible = visibleCount === 1 && !isPointHidden;
          const label =
            pointIssue != null
              ? `${pointIssue.issueIndex}. ${point.label}`
              : point.label;
          const signalsLabel =
            pointIssue != null && pointIssue.signalTitles.length > 0
              ? pointIssue.signalTitles.join(', ')
              : '-';

          return (
            <div
              key={point.seriesId}
              className="rounded-md bg-gray-50 px-2 py-1 text-xs leading-4"
            >
              <div className="flex items-center gap-2">
                <div
                  className="size-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: point.color }}
                />
                <span className="truncate text-gray-700">{label}</span>
                <div className="ml-auto">
                  <Menu
                    triggerNode={
                      <IconButton
                        icon={<MoreVertical className="size-3" />}
                        sizeType="s"
                        outlined={false}
                      />
                    }
                    openMenu={props.lock}
                    closeMenu={props.unlock}
                    options={[
                      {
                        label: isPointHidden
                          ? 'Show on Chart'
                          : 'Hide from Chart',
                        onClick: () => {
                          props.unlock?.();
                          props.onToggleSeries?.(pointIssueName);
                        },
                        disabled: isPointLastVisible,
                      },
                      {
                        label: 'Show Only This',
                        onClick: () => {
                          props.unlock?.();
                          props.onIsolateSeries?.(pointIssueName);
                        },
                        disabled: isPointLastVisible,
                      },
                      {
                        label: 'Reset',
                        onClick: () => {
                          props.unlock?.();
                          props.onResetAll?.();
                        },
                        disabled: !props.isResetEnabled,
                      },
                      {
                        label: 'divider',
                        type: MenuItemType.DIVIDER,
                      },
                      {
                        label: 'View',
                        onClick: () => {
                          props.unlock?.();
                          navigateToIssueDetailPage(
                            pointIssueName,
                            props.dynamicFilterSet,
                            props.datePreset,
                          );
                        },
                      },
                      {
                        label: pointIssue?.favorite ? 'Unfavorite' : 'Favorite',
                        onClick: () => {
                          props.unlock?.();
                          favoriteIssue(pointIssueName, !pointIssue?.favorite);
                        },
                      },
                      {
                        label: 'Hide',
                        onClick: () => {
                          props.unlock?.();
                          hideIssue(pointIssueName, true);
                        },
                      },
                    ]}
                  />
                </div>
              </div>
              <p className="text-gray-600">{`Signals: ${signalsLabel}`}</p>
              <p className="text-gray-600">
                {periodLabel} change:{' '}
                {pointIssue ? (
                  <ChangesLabel
                    comparisonPeriodCount={pointIssue.comparisonPeriodCount}
                    selectedPeriodCount={pointIssue.selectedPeriodCount}
                    className="text-left inline-block text-xs"
                    datePreset={props.datePreset}
                  />
                ) : (
                  '-'
                )}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};
